import { useEffect, useReducer, useRef } from "react";
import { reducer } from "./state/reducer";
import { State } from "./state/state";
import Home from "./pages/Home";
import {
  BrickInventoryEntry,
  loadBrickInventory,
} from "./state/BrickInventory/BrickInventory";
import {
  ActionEntry,
  loadActionEntries,
} from "./state/BrickActions/ActionEntries";
import { Action } from "./state/action";
import {
  BrickPackPurchase,
  loadBrickPackPurchaseHistory,
  loadBrickPackStats,
  loadBrickPackUpgrades,
} from "./state/BrickPackHistory/BrickPackPurchase";
import { SetEntry, SetInventory } from "./state/SetInventory/SetInventory";
import {
  loadFromLocalStorage,
  deserializeState,
  saveToLocalStorage,
} from "./utils/saveLoad";

const getInitialState = (_?: undefined): State => {
  // Create base initial state
  const baseState: State = {
    version: "1.0.0",
    actionTimestamps: [],
    actions: new Map<string, ActionEntry>(loadActionEntries()),
    bricks: new Map<string, BrickInventoryEntry>(loadBrickInventory()),
    sets: new Map<string, SetEntry>(SetInventory),
    brickPackHistory: new Map<string, BrickPackPurchase[]>(
      loadBrickPackPurchaseHistory(),
    ),
    brickPackStats: new Map<string, number>(loadBrickPackStats()),
    brickPackUpgrades: new Map<string, number>(loadBrickPackUpgrades()),
    brickPackBuyAmounts: new Map<string, number>(), // brickPackId -> selected buy amount (defaults to 1)
    brickPackAutoCollect: new Map<string, number>(), // brickPackId -> auto-purchases per second
    setBuyAmounts: new Map<string, number>(), // setId -> selected buy amount (defaults to 1)
    useActionRows: false,
    useInventoryRows: false,
    actionOrder: [], // Custom action order (empty = use default Map order)
    editModeActive: false, // Edit mode for drag-and-reorder (Actions)
    inventoryOrder: [], // Custom inventory order (empty = use default Map order)
    editModeInventory: false, // Edit mode for drag-and-reorder (Inventory)
    helpIconAnimated: true, // Help icon animation enabled by default
    totalBricksCollected: 0,
    bricksCollectedByType: new Map<string, number>(), // brickId -> total count collected
    totalActionsTriggered: 0,
    actionsTriggeredByType: new Map<string, number>(), // actionId -> total count triggered
    totalSetsPurchased: 0,
    setsPurchasedByType: new Map<string, number>(), // setId -> total count purchased
    unlockedAchievements: new Set<string>(), // Set of achievement IDs that have been unlocked
  };

  // Try to load from localStorage
  const savedData = loadFromLocalStorage();
  if (savedData) {
    return deserializeState(savedData, baseState);
  }

  return baseState;
};

export default function Global() {
  const [state, dispatch] = useReducer<React.Reducer<State, Action>>(
    reducer,
    // @ts-ignore - React's lazy initialization pattern (compiles fine, VS Code complains for some reason, no issues on cloudflare)
    null,
    getInitialState,
  );

  const countRef = useRef(0);
  const stateRef = useRef(state);
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Keep stateRef in sync with current state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Auto-create interval for game logic
  useEffect(() => {
    const triggerFunction = () => {
      countRef.current += 1;

      // Execute Auto Create update
      // @ts-ignore - These actions are valid, VS Code incorrectly infers dispatch signature, no issues in cloudflare
      dispatch({ type: "autocreate_every_second" });
      // @ts-ignore - These actions are valid, VS Code incorrectly infers dispatch signature, no issues in cloudflare
      dispatch({ type: "CLEAN_OLD_TIMESTAMPS" });
    };

    const interval = setInterval(triggerFunction, 1000);
    return () => clearInterval(interval);
  }, []);

  // Auto-save interval (every 15 seconds)
  // Uses stateRef to avoid recreating interval on every state change
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      saveToLocalStorage(stateRef.current);
    }, 15000);

    // Store interval ID in ref so we can clear it manually during reset
    autoSaveIntervalRef.current = autoSaveInterval;

    return () => clearInterval(autoSaveInterval);
  }, []); // Empty dependency array - interval runs once and persists

  // Listen for reset events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // If another tab set the reset flag, this tab should reload too
      if (e.key === "__RESET_FLAG__" && e.newValue) {
        // Clear our interval
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current);
        }
        // Clear localStorage and reload
        localStorage.clear();
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Reset handler that clears auto-save interval, clears localStorage, and reloads page
  const handleResetGame = () => {
    // 1. Clear the auto-save interval to prevent any saves during reset
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }

    // 2. Set a flag in localStorage to tell other tabs to reset too
    // (in case user has multiple tabs open - they share localStorage!)
    try {
      localStorage.setItem("__RESET_FLAG__", Date.now().toString());
    } catch (e) {
      console.error("[Reset] Failed to set reset flag:", e);
    }

    // 3. Clear ALL of localStorage
    localStorage.clear();

    // 4. Small delay then reload page
    setTimeout(() => {
      localStorage.clear(); // Clear again for safety
      window.location.reload();
    }, 100);
  };

  return (
    <Home state={state} dispatch={dispatch} onResetGame={handleResetGame} />
  );
}
